import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  Role,
  UserRoleList,
  userRoleListActions,
  userRoleListSelectors,
  roleActions,
  roleSelectors,
} from '@content-platform/web/angular/shared/data-access/application-api';

import { GraphApi, UserProfile } from '@content-platform/web-angular-administration-shared-data-access-graph-api';
import { notificationActions, NotificationType } from '@content-platform/web-angular-shared-ui-notifications';
import { select, Store } from '@ngrx/store';
import { trim } from 'lodash';
import { merge, Observable, Subject, of } from 'rxjs';
import { debounceTime, filter, startWith, switchMap, take, tap, map } from 'rxjs/operators';
import { DisplayNamePipe } from '@content-platform/web-angular-shared-util-pipes';

@Component({
  selector: 'app-role-select-clone',
  templateUrl: './role-select-clone.component.html',
  styleUrls: ['./role-select-clone.component.scss'],
})
export class RoleSelectCloneComponent implements OnInit, OnDestroy {
  userDataForm: FormGroup;
  filteredRoles$: Observable<Role[]>;
  filteredUsersClone$: Observable<UserProfile[]>;
  userRoleList: UserRoleList;
  clonedUserName: string;
  selectedValidUser: boolean = false;

  private searchOptions: { includeLdap: boolean };
  private dpNamePipe: DisplayNamePipe;

  // Roles
  private roles: Role[];
  private updateRoleSubject = new Subject<void>();
  private onDestroy = new Subject<void>();
  private availableRoles: Role[] = [];
  private userRoleIdList: number[];

  constructor(private store: Store<{}>, private graphApi: GraphApi) {}

  ngOnInit() {
    this.createForm();
  }

  createForm() {
    this.dpNamePipe = new DisplayNamePipe();

    this.userDataForm = new FormGroup({
      userSearchCtrlRoles: new FormControl(''),
      userSearchCtrlCloneRoles: new FormControl(''),
    });

    this.store
      .pipe(
        select(roleSelectors.getAllRoleItems),
        tap((roleList) => {
          if (!!roleList && roleList.length === 0) {
            this.store.dispatch(new roleActions.Load());
          }
        }),
        filter((roleList) => !!roleList && roleList.length > 0),
        take(1)
      )
      .subscribe((roles) => {
        this.availableRoles = roles;
        this.roles = roles;
        this.userRoleList = { id: 0, uuid: '0', roles: [] };
        this.updateRoleSubject.next();
      });

    this.filteredRoles$ = merge(
      this.userDataForm.get('userSearchCtrlRoles').valueChanges,
      this.updateRoleSubject.asObservable()
    ).pipe(
      startWith(''),
      debounceTime(50),
      map((state) => {
        const searchRegExp = new RegExp(state || '', 'i');
        return this.availableRoles.filter((role) => searchRegExp.test(role.name));
      })
    );

    this.filteredUsersClone$ = <Observable<UserProfile[]>>(
      this.userDataForm.get('userSearchCtrlCloneRoles').valueChanges.pipe(
        tap(() => (this.selectedValidUser = false)),
        filter((value) => !!value),
        debounceTime(1000),
        switchMap((state: string) => this.graphApi.searchUsers(trim(state), this.searchOptions))
      )
    );
  }

  selectRole(event: MatAutocompleteSelectedEvent): void {
    this.userRoleList = {
      ...this.userRoleList,
      roles: [...this.userRoleList.roles, event.option.value],
    };

    this.userRoleIdList = this.userRoleList.roles.map((role) => role.id);
    this.availableRoles = this.roles.filter((role) => !this.userRoleIdList.includes(role.id));

    // Show Notification that the role has been added to the list of roles successfully
    this.showUserNotification();
  }

  private showUserNotification() {
    this.store.dispatch(
      new notificationActions.Open({
        type: NotificationType.Success,
        inputs: {
          props: {
            message: 'User role successfully selected',
          },
        },
      })
    );
  }

  removeRole(roleId: number): void {
    const roleIds = this.userRoleList.roles
      .filter((role) => {
        return role.id !== roleId;
      })
      .map((role) => role.id);
    this.updateRoleList(roleIds);
  }

  updateRoleList(roleIds: number[]): void {
    this.userRoleList = {
      ...this.userRoleList,
      roles: this.userRoleList.roles.filter((role) => roleIds.includes(role.id)),
    };
    this.availableRoles = this.roles.filter((role) => !roleIds.includes(role.id));
    this.updateRoleSubject.next();
  }

  saveRoles(name: string, uuid: string): Observable<{ id: number; name: string }[]> {
    this.userRoleList.name = name;
    this.userRoleList.uuid = uuid;
    this.userRoleList.id = null;

    this.store.dispatch(new userRoleListActions.Update(this.userRoleList));

    return of(this.userRoleList.roles.map((element) => ({ id: element.id, name: element.name })));
  }

  cloneSelectedUserRoles() {
    const user = this.userDataForm.get('userSearchCtrlCloneRoles').value;

    user['displayName'] = this.dpNamePipe.transform(<UserProfile>user);

    this.clonedUserName = user.displayName;

    this.getUserRoleList(user.objectId).subscribe((data) => {
      this.userRoleList = {
        ...this.userRoleList,
        roles: [...data.roles],
      };

      this.userRoleList.roles = data.roles;
      this.userRoleIdList = this.userRoleList.roles.map((role) => role.id);
      this.availableRoles = this.roles.filter((role) => !this.userRoleIdList.includes(role.id));
      this.updateRoleSubject.next();
    });
  }

  getUserRoleList(id: string) {
    return this.store.pipe(
      select(userRoleListSelectors.getUserRoleListEntities),
      tap((userRoleList) => {
        if (!userRoleList || !userRoleList[id]) {
          this.store.dispatch(new userRoleListActions.LoadById(id));
        }
      }),
      map((userRoleEntities) => userRoleEntities[id]),
      filter((userRoleList) => !!userRoleList),
      take(1)
    );
  }

  getDisplayName(user?: UserProfile): string {
    if (!user || !user.displayName) {
      return '';
    }

    if (!this.dpNamePipe) {
      this.dpNamePipe = new DisplayNamePipe();
    }
    user['displayName'] = this.dpNamePipe.transform(<UserProfile>user);

    return user.displayName;
  }

  selectUser() {
    this.selectedValidUser = true;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
  }
}
